import {Injectable} from '@angular/core';
import {DcpDataService} from '../dcp-data.service';
import {EddlAdobeTrackingData, EddlSearchClickType} from './models/eddl-adobe-tracking-data';
import {CxEvent, EventService} from '@spartacus/core';
import {EddlSearchInitiatedEvent} from './events/eddl-search-initiated-event';
import {EddlSearchNoResultsEvent} from './events/eddl-search-no-results-event';
import {DcpEddlSearchTrackingService} from './dcp-eddl-search-tracking.service';
import {UntilDestroy} from '@ngneat/until-destroy';
import {EddlSearchResultsClickEvent} from './events/eddl-search-results-click-event';
import {DcpEddlWebcomponentMapperService} from '@app/services/analytics/EDDL/dcp-eddl-webcomponent-mapper.service';
import {EddlSearchFilterAppliedEvent} from './events/eddl-search-filter-applied-event';
import {EddlSearchFilterRemovedEvent} from './events/eddl-search-filter-removed-event';
import {EddlSearchFilterClearedEvent} from './events/eddl-search-filter-cleared-event';

export const EDDL_SEARCH_EVENT_NAME = 'search';
export const EDDL_SEARCH_FILTER_APPLIED_EVENT_NAME = 'searchFilterApplied';
export const EDDL_SEARCH_FILTER_REMOVED_EVENT_NAME = 'searchFilterRemoved';
export const EDDL_SEARCH_FILTER_CLEARED_EVENT_NAME = 'searchFilterCleared';

type SearchTrackedEvent =
  | EddlSearchInitiatedEvent
  | EddlSearchNoResultsEvent
  | EddlSearchResultsClickEvent
  | EddlSearchFilterAppliedEvent
  | EddlSearchFilterRemovedEvent
  | EddlSearchFilterClearedEvent;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpEddlSearchDataService extends DcpDataService<EddlAdobeTrackingData> {
  constructor(
    protected searchTrackingService: DcpEddlSearchTrackingService,
    protected eventService: EventService,
    private readonly eddlWebcomponentMapperService: DcpEddlWebcomponentMapperService<{
      search: string;
      currentPage: string;
      pageSize: string;
      query: string;
    }>
  ) {
    super();
  }

  fillData(event: SearchTrackedEvent, data?: EddlAdobeTrackingData): EddlAdobeTrackingData {
    if (!data) {
      data = {};
    }

    // Filter events carry a filters array and never a search term; return
    // early so the search-term branch below (which reads from the tracking
    // service) can't populate stale fields on this payload.
    if (event instanceof EddlSearchFilterAppliedEvent) {
      data.event = EDDL_SEARCH_FILTER_APPLIED_EVENT_NAME;
      data.search = {filters: this.sanitizeFilters(event.filters)};
      return data;
    }
    if (event instanceof EddlSearchFilterRemovedEvent) {
      data.event = EDDL_SEARCH_FILTER_REMOVED_EVENT_NAME;
      data.search = {filters: this.sanitizeFilters(event.filters)};
      return data;
    }
    if (event instanceof EddlSearchFilterClearedEvent) {
      data.event = EDDL_SEARCH_FILTER_CLEARED_EVENT_NAME;
      return data;
    }

    if (!data.search) {
      data.search = {};
    }

    let searchType = 'recently searched';

    if (event?.eventSource === 'WebComponent') {
      const rawTerm = event.searchTerm ?? this.eddlWebcomponentMapperService.getQp()?.search ?? '';
      data.search.term = rawTerm
        .replace(/[^\x00-\x7F]/g, '') // NOSONAR
        .toLowerCase()
        .slice(0, 100);
    } else {
      data.search.term = this.searchTrackingService.getSearchTerm();
      searchType = this.searchTrackingService.getSearchType();
    }

    if (event instanceof EddlSearchInitiatedEvent) {
      data.event = EDDL_SEARCH_EVENT_NAME;
      data.search.location = this.getSearchLocation(searchType);
      // Every dispatch site for this event in this codebase originates from
      // the on-page primary search bar (WC path and legacy Spartacus path
      // alike). No guided-search, widget, or modal search UI exists, so
      // hardcoding the value is safe today; the field is a union type so
      // any future emitter can override it before dispatch.
      data.search.initLocation = 'search bar';
    }
    if (event instanceof EddlSearchNoResultsEvent) {
      data.event = 'searchNoResults';
    }
    if (event instanceof EddlSearchResultsClickEvent) {
      if (event?.eventSource === 'WebComponent') {
        data.search.term = this.eddlWebcomponentMapperService.getQp()?.search ?? '';
      }
      data.event = 'searchClick';
      const pageNumber = this.searchTrackingService.getPageNumber();
      const product = this.searchTrackingService.getProductClicked();

      data.search.clickRank = this.searchTrackingService.getClickRank();
      data.search.clickURL = window.location.href;
      data.search.clickTitle = event?.productName ?? product?.name;

      data.search.location = this.getSearchLocation(searchType);
      if (searchType !== 'keyword search') {
        data.search.clickCategory = searchType as EddlSearchClickType;
      }

      if (pageNumber > 0) {
        data.search.pageNumber = pageNumber;
      }
    }
    return data;
  }

  getData(event: CxEvent): EddlAdobeTrackingData {
    if (
      !(
        event instanceof EddlSearchInitiatedEvent ||
        event instanceof EddlSearchNoResultsEvent ||
        event instanceof EddlSearchResultsClickEvent ||
        event instanceof EddlSearchFilterAppliedEvent ||
        event instanceof EddlSearchFilterRemovedEvent ||
        event instanceof EddlSearchFilterClearedEvent
      )
    ) {
      return undefined;
    }

    return this.fillData(event);
  }

  private getSearchLocation(searchType: string): 'KSD' | 'SRP' {
    return searchType === 'solution suggestions' ? 'KSD' : 'SRP';
  }

  /**
   * Lowercase, strip non-ASCII, and trim each filter entry. Emitters are
   * expected to normalize before dispatch; sanitizing again here guarantees
   * a consistent shape regardless of the caller.
   */
  private sanitizeFilters(
    filters: {category: string; name: string}[] | undefined
  ): {category: string; name: string}[] {
    if (!filters?.length) {
      return [];
    }
    return filters.map((f) => ({
      category: (f?.category ?? '').replace(/[^\x00-\x7F]/g, '').toLowerCase().trim(), // NOSONAR
      name: (f?.name ?? '').replace(/[^\x00-\x7F]/g, '').toLowerCase().trim(), // NOSONAR
    }));
  }
}
